// ============================================================================
// Content Review Types - Matching backend DTOs
// ============================================================================

export enum ContentStatus {
  UPLOADING = 'UPLOADING',
  PROCESSING = 'PROCESSING',
  PENDING_REVIEW = 'PENDING_REVIEW',
  PENDING_IMPORT = 'PENDING_IMPORT',
  READY = 'READY',
  FAILED = 'FAILED',
  REJECTED = 'REJECTED',
}

export enum ContentType {
  VIDEO = 'VIDEO',
  PHOTO = 'PHOTO',
}

export enum ContentSource {
  UPLOAD = 'UPLOAD',
  SORA = 'SORA',
  AI_GENERATED = 'AI_GENERATED',
  TIKTOK_IMPORT = 'TIKTOK_IMPORT',
  INSTAGRAM_IMPORT = 'INSTAGRAM_IMPORT',
}

// ============================================================================
// Content Owner
// ============================================================================

export interface ContentOwner {
  id: number;
  name: string;
  email: string;
  profileImage: string | null;
}

// ============================================================================
// Review Audit
// ============================================================================

export interface ReviewedBy {
  adminId: number;
  name: string;
}

// ============================================================================
// Content List Item (from GET /admin/content)
// ============================================================================

export interface AdminContentItemDto {
  id: string;
  type: ContentType;
  status: ContentStatus;
  source: ContentSource;

  // Media URLs
  posterUrl: string | null;
  videoUrl: string | null;

  // Metadata
  caption: string | null;
  serviceTag: string | null;
  groupSessionId: number | null;
  width: number | null;
  height: number | null;
  durationSec: number | null;

  // Engagement
  viewCount: number;
  likesCount: number;

  // Owner
  owner: ContentOwner;

  // Review audit trail
  reviewedBy: ReviewedBy | null;
  reviewedAt: string | null;
  rejectionReason: string | null;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Content Detail (from GET /admin/content/:id)
// ============================================================================

export interface AdminContentDetailDto extends AdminContentItemDto {
  // AI generation
  aiPrompt: string | null;

  // Blob storage paths
  sourcePath: string | null;
  videoMasterPath: string | null;
  posterPath: string | null;
  externalPosterUrl: string | null;

  // Social import metadata
  socialImport: {
    platform: 'tiktok' | 'instagram';
    externalId: string;
    title: string | null;
    shareUrl: string | null;
  } | null;

  // Group session
  groupSession: {
    id: number;
    title: string;
    status: string;
  } | null;
}

// ============================================================================
// API Query / Response Types
// ============================================================================

export interface ContentListQueryParams {
  status?: ContentStatus | 'ALL';
  type?: ContentType;
  source?: ContentSource;
  ownerId?: number;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ContentPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ContentListResponse {
  success: boolean;
  data: {
    items: AdminContentItemDto[];
    pagination: ContentPagination;
  };
}

export interface ContentDetailResponse {
  success: boolean;
  data: AdminContentDetailDto;
}

export interface ContentActionResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    status: string;
    reviewedBy: ReviewedBy;
    reviewedAt: string;
    rejectionReason?: string | null;
  };
}

export interface BulkActionResponse {
  success: boolean;
  data: {
    approvedCount?: number;
    rejectedCount?: number;
    skippedCount: number;
    skipped: {
      id: string;
      currentStatus: string;
      reason: string;
    }[];
  };
}

export interface ContentSummary {
  pendingReview: number;
  pendingImport: number;
  ready: number;
  rejected: number;
  processing: number;
  uploading: number;
  failed: number;
  total: number;
}

export interface ContentSummaryResponse {
  success: boolean;
  data: ContentSummary;
}

export interface TikTokUploadResponse {
  success: boolean;
  mediaAssetId: string;
  cdnUrl: string;
  tiktokVideoId: string;
}
